import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useMontos } from '../../context/MontosContext';
import { formatearPrecio, formatearPrecioCompacto } from '../../utils/formatearPrecio';
import { formatDateISO, getTodayISO } from '../../utils/dateUtils';
import { LuChevronRight } from 'react-icons/lu';

// Resumen de Finanzas por período (28-oct-2026). Reemplaza Balance + Cobranza: un solo número grande
// (lo que te queda), la cuenta debajo, lo que falta cobrar, cómo viene semana a semana y los trabajos.
// Por período (7/30/60/90 días, un mes o desde–hasta) porque se trabaja y se cobra todos los días.
const RAPIDOS = [{ id: '7', label: '7 días' }, { id: '30', label: '30 días' }, { id: '60', label: '60' }, { id: '90', label: '90' }];
const CLAVE = 'finanzas_periodo';
const menosDias = (n) => { const d = new Date(); d.setDate(d.getDate() - (n - 1)); return formatDateISO(d); };
const ddmm = (iso) => { const [, m, d] = String(iso).slice(0, 10).split('-'); return `${Number(d)}/${Number(m)}`; };
const ddmmaa = (iso) => { const [a, m, d] = String(iso).slice(0, 10).split('-'); return `${d}/${m}/${a.slice(2)}`; };

function periodoInicial() {
    try { const p = JSON.parse(localStorage.getItem(CLAVE) || 'null'); if (p?.modo) return p; } catch { /* sin storage */ }
    return { modo: '30' };
}

export default function TabResumen() {
    const { ocultar } = useMontos();
    const [periodo, setPeriodo] = useState(periodoInicial);
    const [otro, setOtro] = useState(false);
    const [mesOtro, setMesOtro] = useState(getTodayISO().slice(0, 7));
    const [d0, setD0] = useState(''); const [d1, setD1] = useState('');
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [verPend, setVerPend] = useState(false);
    const [cuantos, setCuantos] = useState(15);

    const { desde, hasta, titulo } = useMemo(() => {
        const hoy = getTodayISO();
        if (periodo.modo === 'mes') {
            const [y, m] = periodo.mes.split('-').map(Number);
            const fin = formatDateISO(new Date(y, m, 0));
            const nombre = new Date(y, m - 1, 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
            return { desde: `${periodo.mes}-01`, hasta: fin < hoy ? fin : hoy, titulo: nombre };
        }
        if (periodo.modo === 'rango') return { desde: periodo.desde, hasta: periodo.hasta, titulo: `del ${ddmmaa(periodo.desde)} al ${ddmmaa(periodo.hasta)}` };
        const n = Number(periodo.modo);
        return { desde: menosDias(n), hasta: hoy, titulo: `últimos ${n} días` };
    }, [periodo]);

    const elegir = (p) => {
        setPeriodo(p); setOtro(false); setCuantos(15);
        try { localStorage.setItem(CLAVE, JSON.stringify(p)); } catch { /* sin storage */ }
    };

    useEffect(() => {
        let vivo = true;
        setCargando(true);
        api.get('/finanzas/resumen', { params: { desde, hasta } })
            .then(r => { if (vivo) setDatos(r.data); })
            .catch(e => { if (vivo) toast.error(e?.response?.data?.mensaje || 'No se pudo cargar el resumen'); })
            .finally(() => { if (vivo) setCargando(false); });
        return () => { vivo = false; };
    }, [desde, hasta]);

    const $ = (v) => (ocultar ? '••••' : `$ ${formatearPrecio(Math.round(Number(v) || 0))}`);
    const $c = (v) => (ocultar ? '••' : formatearPrecioCompacto(Math.round(Number(v) || 0)));
    const chip = (on) => `shrink-0 h-9 px-3 rounded-xl text-label font-black border ${on ? 'bg-[#C9341F] text-white border-transparent' : 'bg-card text-secondary border-black/[0.06] dark:border-white/[0.08]'}`;

    const d = datos;
    const vs = d && Number(d.anterior?.teQueda) ? Math.round((Number(d.teQueda) - Number(d.anterior.teQueda)) / Math.abs(Number(d.anterior.teQueda)) * 100) : null;
    const maxSerie = d ? Math.max(1, ...d.serie.map(x => Math.abs(Number(x.teQueda)))) : 1;
    const hoyISO = getTodayISO();
    const tecTxt = d?.porTecnico?.length ? ` (${d.porTecnico.map(t => t.nombre).join(', ')})` : '';
    const pend = d?.faltaCobrar;

    return (
        <div className="space-y-3">
            {/* Período */}
            <div>
                <div className="flex gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1">
                    {RAPIDOS.map(r => <button key={r.id} type="button" onClick={() => elegir({ modo: r.id })} className={chip(periodo.modo === r.id)}>{r.label}</button>)}
                    <button type="button" onClick={() => setOtro(v => !v)} className={chip(['mes', 'rango'].includes(periodo.modo) || otro)}>Otro ▾</button>
                </div>
                <p className="mt-1.5 px-1 text-caption text-muted">Del {ddmmaa(desde)} al {ddmmaa(hasta)}{cargando ? ' · cargando…' : ''}</p>
                {otro && (
                    <div className="mt-2 p-3 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.08] space-y-2.5">
                        <div className="flex items-center gap-2">
                            <span className="w-16 text-caption font-bold text-muted">Un mes</span>
                            <input type="month" value={mesOtro} onChange={e => setMesOtro(e.target.value)} className="flex-1 h-10 px-3 rounded-xl bg-chip text-ink text-body font-bold outline-none" />
                            <button type="button" onClick={() => mesOtro && elegir({ modo: 'mes', mes: mesOtro })} className="h-10 px-3 rounded-xl bg-ink text-page text-label font-black">Ver</button>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-16 text-caption font-bold text-muted">Desde</span>
                            <input type="date" value={d0} onChange={e => setD0(e.target.value)} className="flex-1 min-w-0 h-10 px-2 rounded-xl bg-chip text-ink text-body font-bold outline-none" />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-16 text-caption font-bold text-muted">Hasta</span>
                            <input type="date" value={d1} onChange={e => setD1(e.target.value)} className="flex-1 min-w-0 h-10 px-2 rounded-xl bg-chip text-ink text-body font-bold outline-none" />
                            <button type="button" onClick={() => {
                                if (!d0 || !d1) { toast.error('Elegí las dos fechas'); return; }
                                if (d1 < d0) { toast.error('La fecha hasta es anterior a desde'); return; }
                                elegir({ modo: 'rango', desde: d0, hasta: d1 });
                            }} className="h-10 px-3 rounded-xl bg-ink text-page text-label font-black">Ver</button>
                        </div>
                    </div>
                )}
            </div>

            {!d ? <div className="h-72 rounded-3xl bg-card animate-pulse" /> : (
                <div className={`space-y-3 ${cargando ? 'opacity-60' : ''}`}>
                    {/* Te queda + la cuenta */}
                    <div className={`p-4 rounded-3xl border-2 ${Number(d.teQueda) < 0 ? 'border-[#C9341F]/50 bg-[#C9341F]/10' : 'border-[#16A34A]/50 bg-[#16A34A]/10'}`}>
                        <p className="text-label font-black uppercase tracking-widest text-muted">Te queda · {titulo}</p>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <p className={`text-[2rem] leading-none font-black ${Number(d.teQueda) < 0 ? 'text-brand-red' : 'text-brand-green'}`}>{$(d.teQueda)}</p>
                            {vs !== null && !ocultar && (
                                <span className={`h-7 px-2 rounded-lg inline-flex items-center text-label font-black ${vs >= 0 ? 'bg-[#16A34A]/15 text-brand-green' : 'bg-[#C9341F]/15 text-brand-red'}`}>{vs >= 0 ? '▲' : '▼'} {Math.abs(vs)}%</span>
                            )}
                        </div>
                        <p className="mt-1 text-caption text-muted">vs. los {d.dias} días anteriores ({$(d.anterior.teQueda)})</p>
                        <div className="mt-3 pt-2 border-t border-black/[0.08] dark:border-white/[0.08] text-caption">
                            {[
                                ['Entró (cobrado)', d.cobrado, 'text-brand-amber', null],
                                [`− Técnicos${tecTxt}`, d.tecnicos, 'text-brand-red', 'La mitad de lo que queda de cada trabajo que hizo otro técnico'],
                                [`− Impuestos ${d.impuestoPct}%`, d.impuestos, 'text-brand-red', 'Sobre lo cobrado con factura o transferencia'],
                                ['− Repuestos', d.repuestos, 'text-brand-red', 'Lo que te costaron'],
                                ['− Gastos', d.gastos, 'text-brand-red', null],
                            ].map(([l, v, c, ayuda]) => (
                                <div key={l} className="flex justify-between items-start gap-3 py-1.5">
                                    <span className="text-secondary">{l}{ayuda && <span className="block text-label text-muted">{ayuda}</span>}</span>
                                    <b className={`shrink-0 ${c}`}>{$(v)}</b>
                                </div>
                            ))}
                            <div className="flex justify-between items-center gap-3 pt-2 mt-1 border-t border-black/[0.08] dark:border-white/[0.08]">
                                <b className="text-ink text-body">= Te queda</b>
                                <b className={`text-body-lg ${Number(d.teQueda) < 0 ? 'text-brand-red' : 'text-brand-green'}`}>{$(d.teQueda)}</b>
                            </div>
                        </div>
                    </div>

                    {/* Falta cobrar (siempre todo) */}
                    {pend && Number(pend.total) > 0 && (
                        <div className="rounded-2xl border border-[#D48800]/40 bg-[#D48800]/10 overflow-hidden">
                            <button type="button" onClick={() => setVerPend(v => !v)} className="w-full p-3.5 flex items-center justify-between gap-3 text-left">
                                <span className="min-w-0">
                                    <span className="block text-body font-black text-ink">Falta cobrar</span>
                                    <span className="block text-caption text-muted truncate">
                                        {[...pend.empresas.map(e => e.concepto.replace(' · cierre', '')), pend.trabajos.length ? `${pend.trabajos.length} trabajo${pend.trabajos.length !== 1 ? 's' : ''}` : null].filter(Boolean).join(' + ')} · de cualquier fecha
                                    </span>
                                </span>
                                <span className="shrink-0 flex items-center gap-1">
                                    <b className="text-body-lg text-brand-amber whitespace-nowrap">{$(pend.total)}</b>
                                    <LuChevronRight size={16} className={`text-muted transition-transform ${verPend ? 'rotate-90' : ''}`} />
                                </span>
                            </button>
                            {verPend && (
                                <div className="px-3.5 pb-3 space-y-1.5">
                                    {pend.empresas.map(e => (
                                        <div key={`e${e.clienteId}`} className="flex justify-between gap-3 py-1.5 border-t border-black/[0.06] dark:border-white/[0.06] text-caption">
                                            <span className="min-w-0"><b className="text-ink">{e.concepto}</b>
                                                <span className="block text-muted">{[Number(e.facturado) > 0 ? `facturado ${$(e.facturado)}` : null, e.equiposSinFacturar ? `${e.equiposSinFacturar} equipos sin facturar (estimado, sin IVA)` : null].filter(Boolean).join(' · ')}</span></span>
                                            <b className="shrink-0 text-ink">{$(e.monto)}</b>
                                        </div>
                                    ))}
                                    {pend.trabajos.map(t => (
                                        <div key={t.id} className="flex justify-between gap-3 py-1.5 border-t border-black/[0.06] dark:border-white/[0.06] text-caption">
                                            <span className="min-w-0"><b className="text-ink truncate block">{t.concepto}</b>
                                                <span className="block text-muted truncate">{[t.fecha ? ddmm(t.fecha) : null, t.estado === 'FACTURADO' ? 'facturado' : 'hecho', t.detalle].filter(Boolean).join(' · ')}</span></span>
                                            <b className="shrink-0 text-ink">{$(t.monto)}</b>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Semana a semana */}
                    {d.serie.length > 1 && (
                        <div className="p-3.5 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.08]">
                            <p className="text-label font-black uppercase tracking-widest text-muted">Te queda por {d.serie[0].tipo === 'mes' ? 'mes' : 'semana'}</p>
                            <div className="mt-3 flex items-end gap-1.5 h-28 border-b border-black/10 dark:border-white/10">
                                {d.serie.map(x => {
                                    const v = Number(x.teQueda);
                                    const enCurso = x.hasta >= hoyISO;
                                    return (
                                        <div key={x.desde} className="flex-1 min-w-0 h-full flex flex-col justify-end items-center gap-1">
                                            <span className="text-[10px] font-bold text-muted">{$c(v)}</span>
                                            <div className={`w-full rounded-t-md ${v < 0 ? 'bg-[#C9341F]' : enCurso ? 'bg-[#4ADE80]' : 'bg-[#16A34A]/60'}`}
                                                style={{ height: `${Math.max(3, Math.abs(v) / maxSerie * 78)}%` }} title={`${ddmm(x.desde)} al ${ddmm(x.hasta)}`} />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-1 flex gap-1.5">
                                {d.serie.map(x => <span key={x.desde} className="flex-1 min-w-0 text-center text-[10px] text-muted truncate">{x.tipo === 'mes' ? new Date(`${x.desde}T12:00:00`).toLocaleDateString('es-AR', { month: 'short' }) : ddmm(x.desde)}</span>)}
                            </div>
                            {d.serie.some(x => x.hasta >= hoyISO) && <p className="mt-1.5 text-label text-muted">La barra más clara es la {d.serie[0].tipo === 'mes' ? 'del mes' : 'semana'} en curso (todavía incompleta).</p>}
                        </div>
                    )}

                    {/* Trabajos cobrados del período */}
                    <p className="pt-1 px-1 text-label font-black uppercase tracking-widest text-muted">Lo cobrado · {titulo} <span className="text-secondary">({d.trabajos.length})</span></p>
                    {d.trabajos.length === 0 ? (
                        <p className="py-8 text-center text-caption text-muted rounded-2xl bg-card">No se cobró nada en este período.</p>
                    ) : (
                        <div className="space-y-1.5">
                            {d.trabajos.slice(0, cuantos).map(t => (
                                <div key={t.id} className="px-3.5 py-2.5 rounded-2xl bg-card border border-black/[0.05] dark:border-white/[0.06] flex justify-between gap-3">
                                    <span className="min-w-0">
                                        <span className="block text-body font-black text-ink truncate">{t.concepto}</span>
                                        <span className="block text-caption text-muted truncate">
                                            {[t.grupo ? t.detalle : null, ddmm(t.fecha), t.venta ? 'venta' : null, t.cobro, t.tecnico].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>
                                    <span className="shrink-0 text-right">
                                        <b className="block text-body text-ink">{$(t.cobrado)}</b>
                                        <span className={`block text-caption font-bold ${Number(t.teDeja) < 0 ? 'text-brand-red' : 'text-brand-green'}`}>te deja {$(t.teDeja)}</span>
                                    </span>
                                </div>
                            ))}
                            {d.trabajos.length > cuantos && (
                                <button type="button" onClick={() => setCuantos(n => n + 15)} className="w-full h-10 rounded-xl bg-chip text-ink text-label font-black">
                                    Ver {Math.min(15, d.trabajos.length - cuantos)} más · quedan {d.trabajos.length - cuantos}
                                </button>
                            )}
                        </div>
                    )}
                    {!ocultar && Number(d.cobradoEfectivo) > 0 && (
                        <p className="px-1 text-caption text-muted">De lo cobrado, {$(d.cobradoEfectivo)} fue en efectivo sin factura (no paga impuesto).</p>
                    )}
                </div>
            )}
        </div>
    );
}
